import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchReadingPDFs,
  uploadReadingPDF,
  deleteReadingPDF,
  getDailyUsage,
} from "../api";

import Surface from "../theme/Surface";
import PdfReader from "./reading/PdfReader";

/**
 * Reading — upload & read Arabic PDFs in-app. Tapping an Arabic word offers a
 * translation (AI) and the option to add it to the Word Bank. The reader
 * working surface lives in reading/PdfReader.js, shared with the public
 * /reader page.
 */
export default function Reading({ onClose, onTopicCreated = () => {} }) {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null); // open pdf | null (library)
  const [uploading, setUploading] = useState(false);
  // Admin-controlled reading settings (default on).
  const [settings, setSettings] = useState({ rememberPage: true, ocrHighRes: true });
  const fileInputRef = useRef(null);

  useEffect(() => {
    getDailyUsage()
      .then((u) => setSettings({
        rememberPage: u.limits?.reading_remember_page !== false,
        ocrHighRes: u.limits?.reading_ocr_high_res !== false,
        translationMaxChars: u.limits?.translation_max_chars ?? 300,
        translationRemaining: u.limits?.max_daily_sentence_translations != null
          ? Math.max(0, u.limits.max_daily_sentence_translations - (u.sentence_translation_count || 0))
          : null,
      }))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchReadingPDFs()
      .then((d) => setPdfs(d.pdfs || []))
      .catch(() => setError("Could not load your library."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadReadingPDF(file, file.name.replace(/\.pdf$/i, ""));
      load();
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (pdf) => {
    const prev = pdfs;
    setPdfs((list) => list.filter((p) => p.id !== pdf.id));
    try {
      await deleteReadingPDF(pdf.id);
    } catch {
      setPdfs(prev);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4" onClick={onClose}>
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-4xl h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {active && (
              <button
                onClick={() => setActive(null)}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                aria-label="Back to library"
                title="Back to library"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-ink-800 truncate">
                {active ? active.title : "Reading"}
              </h2>
              {!active && <p className="text-xs text-ink-400">Read Arabic PDFs — tap a word to translate or save it.</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {active ? (
          <PdfReader pdf={active} onTopicCreated={onTopicCreated} settings={settings} />
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 scroll-elegant">
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={handleUpload} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full px-3 py-2.5 rounded-xl border border-ink-200 bg-ink-50 hover:bg-ink-100 text-ink-700 text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {uploading ? "Uploading…" : "Upload a PDF"}
            </button>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

            {loading ? (
              <p className="text-sm text-ink-400 mt-4">Loading…</p>
            ) : pdfs.length === 0 ? (
              <p className="text-sm text-ink-400 mt-4">No books yet. Upload a PDF to start reading.</p>
            ) : (
              <ul className="list-none p-0 mt-4 space-y-2">
                {pdfs.map((p) => (
                  <Surface as="li" key={p.id} glass interactive nudge className="flex items-center justify-between gap-2 px-3 py-3">
                    <button onClick={() => setActive(p)} className="text-left flex-1 min-w-0 flex items-center gap-2">
                      <svg className="w-5 h-5 shrink-0" style={{ color: "var(--gold-dim)" }} viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 2a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H6z" opacity=".15" />
                        <path d="M14 2v6h6" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      <span className="truncate font-cormorant text-lg font-semibold" style={{ color: "var(--parchment)" }}>{p.title || p.filename}</span>
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="p-1.5 rounded shrink-0 transition-colors hover:brightness-125"
                      style={{ color: "var(--tag-rose)" }}
                      aria-label="Delete"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </Surface>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
